/**
 * Route-level gate on a runtime feature toggle. Renders its children only when
 * the feature's resolved toggle is ON (runtime value, or the build-time env
 * default while it loads); otherwise it redirects.
 *
 * Used for the `/help` route: the build-time HELP_CENTER_ENABLED flag still
 * guards route REGISTRATION in App.tsx, and this adds the super-admin runtime
 * toggle on top so an off toggle makes /help unreachable without a redeploy.
 * Because it falls back to the env default while the toggle loads, a normal ON
 * build renders the page immediately with no flash-then-redirect.
 *
 * NB this is a distinct concern from the plan-based `@/components/ui/FeatureGate`
 * (which gates on the org's subscribed features and shows an upgrade card).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureToggle } from './useFeatureToggles';
import type { FeatureToggleName } from './featureToggles';

interface FeatureToggleGateProps {
  feature: FeatureToggleName;
  /** Where to send the user when the feature is toggled off. Defaults to home. */
  redirectTo?: string;
  children: ReactNode;
}

export function FeatureToggleGate({
  feature,
  redirectTo = '/',
  children,
}: FeatureToggleGateProps) {
  const enabled = useFeatureToggle(feature);
  if (!enabled) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}
